import { AnimatePresence, motion, MotionConfig } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { dismissToast, useToastStore } from './toast-store.js';
import type { ToastRecord } from './toast-store.js';

function OnboardingToastContent({ toast }: { toast: ToastRecord }) {
  const { t } = useTranslation('common');
  return (
    <div className="flex flex-col gap-3">
      <div className="min-w-0">
        {toast.title ? <p className="text-sm font-medium text-primary">{toast.title}</p> : null}
        {toast.description ? (
          <p className="mt-1 text-sm text-secondary">{toast.description}</p>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        {toast.showNext ? (
          <button
            type="button"
            data-testid="toast-onboarding-next"
            onClick={() => toast.onNext?.()}
            className="rounded border border-[#333] px-3 py-1 text-sm text-white transition-colors hover:bg-white/10 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            {t('ui.next')}
          </button>
        ) : null}
        <button
          type="button"
          data-testid="toast-onboarding-dismiss"
          onClick={() => toast.onDismiss?.()}
          className="px-3 py-1 text-sm text-[#666] transition-colors hover:text-[#999] cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {t('ui.dismiss')}
        </button>
      </div>
    </div>
  );
}

export function ToastRegion() {
  const { t } = useTranslation('common');
  const toasts = useToastStore();

  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      data-testid="toast-region"
      className="pointer-events-none fixed bottom-6 right-6 z-50 flex w-[min(100%,36rem)] flex-col gap-3"
    >
      {/* AC6: toast repositioning is functional feedback, not decorative —
          carve the subtree out of the app-root <MotionConfig reducedMotion="user">
          so the layout spring keeps running when the user prefers reduced motion. */}
      <MotionConfig reducedMotion="never">
        <AnimatePresence initial={false}>
          {toasts.map((toast) => {
            const isOnboarding = toast.tone === 'onboarding';
            const dismissible = !isOnboarding && (toast.dismissible ?? toast.tone === 'error');

            return (
              <motion.section
                key={toast.id}
                layout
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 12, scale: 0.98 }}
                transition={{
                  duration: 0.18,
                  ease: 'easeOut',
                  layout: { type: 'spring', stiffness: 300, damping: 25 },
                }}
                role={toast.tone === 'error' ? 'alert' : 'status'}
                className="pointer-events-auto relative rounded-lg border border-white/[0.05] bg-[rgba(20,20,20,0.9)] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.35)] backdrop-blur-sm"
              >
                {isOnboarding ? (
                  <OnboardingToastContent toast={toast} />
                ) : (
                  <>
                    <div className="min-w-0">
                      {toast.content ? (
                        toast.content
                      ) : (
                        <>
                          {toast.title ? (
                            <p className="text-sm font-medium text-primary">{toast.title}</p>
                          ) : null}
                          {toast.description ? (
                            <p className="mt-1 text-sm text-secondary">{toast.description}</p>
                          ) : null}
                        </>
                      )}
                    </div>

                    {dismissible ? (
                      <button
                        type="button"
                        data-testid="toast-dismiss"
                        onClick={() => dismissToast(toast.id)}
                        className="btn-press absolute right-3 top-3 inline-flex h-[24px] w-[24px] items-center justify-center rounded text-muted transition-colors hover:bg-white/[0.04] hover:text-primary cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
                        aria-label={t('ui.dismissNotification')}
                      >
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M2.5 2.5 L9.5 9.5 M9.5 2.5 L2.5 9.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      </button>
                    ) : null}
                  </>
                )}
              </motion.section>
            );
          })}
        </AnimatePresence>
      </MotionConfig>
    </div>
  );
}
